import { DatePicker } from "@shared/ui";
import { formatFilterLabelWithCount } from "@widgets/CampaignBrief/lib/campaignBriefFilterFacetCounts";
import { useBriefInfluencerFilterStore } from "@widgets/CampaignBrief/store/useBriefInfluencerFilterStore";
import { SearchMultiSelectFilter } from "@shared/ui/filters/SearchMultiSelectFilter";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import styles from "./InfluencerCommercialFilter.module.scss";

interface InfluencerCommercialFilterOption {
  label: string;
  value: string;
}

interface InfluencerCommercialFilterProps {
  confirm: () => void;
  close: () => void;
  options: InfluencerCommercialFilterOption[];
  getOptionCount?: (value: string) => number | undefined;
  searchPlaceholder?: string;
  before?: React.ReactNode;
}

type DateRangeValue = [Dayjs | null, Dayjs | null];

const toDateRangeValue = (range?: {
  from?: string;
  to?: string;
}): DateRangeValue => [
  range?.from ? dayjs(range.from) : null,
  range?.to ? dayjs(range.to) : null,
];

export function InfluencerCommercialFilter({
  confirm,
  close,
  options,
  getOptionCount,
  searchPlaceholder = "Search influencer commercial status",
  before,
}: InfluencerCommercialFilterProps) {
  const { feFilters, setFilters, saveFilters } =
    useBriefInfluencerFilterStore();
  const selectedRange = feFilters.influencerCostDate;
  const [dateValue, setDateValue] = useState<DateRangeValue>(() =>
    toDateRangeValue(selectedRange)
  );

  useEffect(() => {
    setDateValue(toDateRangeValue(selectedRange));
  }, [selectedRange?.from, selectedRange?.to]);

  const selectedValues = options
    .filter(
      (option) =>
        feFilters.statusFilters?.[
          option.value as keyof typeof feFilters.statusFilters
        ]
    )
    .map((option) => option.value);

  const handleApply = (values: string[]) => {
    const newStatusFilters = { ...feFilters.statusFilters };

    options.forEach((option) => {
      newStatusFilters[option.value as keyof typeof newStatusFilters] =
        values.includes(option.value);
    });

    const influencerCostDate =
      dateValue[0] || dateValue[1]
        ? {
            from: dateValue[0]?.startOf("day").toISOString(),
            to: dateValue[1]
              ? dateValue[1].endOf("day").toISOString()
              : undefined,
          }
        : undefined;

    setFilters({
      ...feFilters,
      statusFilters: newStatusFilters,
      influencerCostDate,
    });
    saveFilters();
  };

  return (
    <SearchMultiSelectFilter
      confirm={confirm}
      close={close}
      before={before}
      options={options.map((option) => ({
        label: formatFilterLabelWithCount(
          option.label,
          getOptionCount?.(option.value)
        ),
        value: option.value,
      }))}
      selectedValues={selectedValues}
      onApply={handleApply}
      searchPlaceholder={searchPlaceholder}
      extra={
        <div className={styles.dateSection}>
          <p className={styles.dateLabel}>Date</p>
          <DatePicker.RangePicker
            value={dateValue}
            onChange={(dates) => {
              if (!dates || dates.length !== 2) {
                setDateValue([null, null]);
                return;
              }
              setDateValue([
                dates[0] ? dates[0].startOf("day") : null,
                dates[1] ? dates[1].endOf("day") : null,
              ]);
            }}
            allowEmpty={[true, true]}
            placeholder={["From", "To"]}
            className={styles.datePicker}
          />
        </div>
      }
    />
  );
}
